import * as Ariakit from '@ariakit/react'
import * as React from 'react'
import { Portal } from 'reakit'

import { notifyOnVisibilityChange } from '../../../app/shared/documentVisibility'
import { TooltipArrow } from '../tooltip/tooltip-arrow'
import { TooltipBox } from '../tooltip/tooltip-box'

function TooltipInner(props: any) {
  const tooltipContext = Ariakit.useTooltipContext()
  const currentPlacement = tooltipContext?.useState('currentPlacement') ?? 'auto'

  /**
   * Ignore focus re-trigger (do not show tooltip) when page visibility/focus state changes
   *  - some browsers re-trigger focus on `activeElement` when document becomes visible
   */
  React.useEffect(() => {
    const subscription = notifyOnVisibilityChange((visible) => {
      if (!visible) tooltipContext?.hide()
    })
    return () => {
      subscription.unsubscribe()
    }
    // Changing this to `tooltip` dependency will run this effect on every render
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    // Temporary fix for modal reakit dialogs
    <Portal>
      <Ariakit.Tooltip
        modal={false}
        focusable={false}
        unmountOnHide
        className="finviz-popover pointer-events-none z-dialog"
      >
        <TooltipBox data-placement={currentPlacement}>
          <Ariakit.TooltipArrow
            size={11}
            render={
              <div>
                <TooltipArrow placement={currentPlacement} />
              </div>
            }
          />
          {props.children}
        </TooltipBox>
      </Ariakit.Tooltip>
    </Portal>
  )
}

export function SelectTooltipWrapper({ content, children }: { content?: React.ReactNode; children: JSX.Element }) {
  const context = Ariakit.useSelectContext()
  const isPopoverOpen = context?.useState('open')
  const hasShownTooltip = React.useRef(false)

  React.useEffect(() => {
    if (isPopoverOpen) hasShownTooltip.current = true
  }, [isPopoverOpen])

  if (!content) return null

  return (
    <Ariakit.TooltipProvider placement="bottom">
      <Ariakit.TooltipAnchor
        render={children}
        onFocusVisible={(ev) => {
          // Do not open again unless user focuses the button again (or presses another ESC)
          if (hasShownTooltip.current) ev.preventDefault()
          hasShownTooltip.current = false
        }}
      />
      <TooltipInner>{content}</TooltipInner>
    </Ariakit.TooltipProvider>
  )
}
